import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './components/LoginPage';
import UploadPage from './components/UploadPage';
import ArticlesList from './components/ArticlesList';
import ArticleEditor from './components/ArticleEditor';
import WorkflowHub from './components/WorkflowHub';
import V2Workflow from './components/V2Workflow';
import V2Projects from './components/V2Projects';
import V3Workflow from './components/V3Workflow';
import V5BatchSystem from './components/V5BatchSystem';
import VideosPage from './components/VideosPage';
import RiversideVideos from './components/RiversideVideos';
import SettingsPage from './components/SettingsPage';
import AdminPage from './components/AdminPage';
import ExtractionMonitor from './components/ExtractionMonitor';
import SMPlanner from './components/SMPlanner';
import CategoriesPage from './components/CategoriesPage';
import BlogsPage from './components/BlogsPage';
import AgentPage from './components/AgentPage';
import ContentManager from './components/ContentManager';
import ContentManagerV2 from './components/ContentManagerV2';
import { FileText, FolderOpen, LogOut, Settings, Database, Video, Calendar, ChevronDown, Tag, BookOpen, MoreHorizontal, Sparkles, LayoutGrid } from 'lucide-react';

// Navigation component
const Navigation = () => {
  const { user, signOut } = useAuth();
  const [videosDropdownOpen, setVideosDropdownOpen] = React.useState(false);
  const [moreDropdownOpen, setMoreDropdownOpen] = React.useState(false);

  if (!user) return null;

  return (
    <nav className="bg-white shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/v2" className="flex items-center space-x-2">
              <FileText className="h-8 w-8 text-purple-600" />
              <span className="text-xl font-bold text-gray-900">YPN Video Creator</span>
            </Link>
          </div>
          <div className="flex items-center space-x-8">
            <Link 
              to="/v2-projects" 
              className="flex items-center space-x-2 text-gray-600 hover:text-purple-600 transition-colors"
            >
              <FolderOpen className="h-5 w-5" />
              <span>My Projects</span>
            </Link>
                <Link 
                  to="/v5" 
                  className="flex items-center space-x-2 text-gray-600 hover:text-purple-600 transition-colors"
                >
                  <Database className="h-5 w-5" />
                  <span>Scripts</span>
                </Link>
                <div 
                  className="relative"
                  onMouseEnter={() => setVideosDropdownOpen(true)}
                  onMouseLeave={() => setVideosDropdownOpen(false)}
                >
                  <button className="flex items-center space-x-2 text-gray-600 hover:text-purple-600 transition-colors">
                    <Video className="h-5 w-5" />
                    <span>Videos</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  {/* Dropdown menu */}
                  {videosDropdownOpen && (
                    <div className="absolute left-0 top-full pt-2 z-50">
                      <div className="bg-white shadow-lg border border-gray-200 rounded-lg w-48">
                        <Link 
                          to="/videos" 
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:bg-purple-50 hover:text-purple-600 transition-colors rounded-t-lg"
                        >
                          <span>Article Videos</span>
                        </Link>
                        <Link 
                          to="/videos/riverside" 
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:bg-purple-50 hover:text-purple-600 transition-colors rounded-b-lg"
                        >
                          <span>Riverside</span>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
                <Link 
                  to="/sm-planner" 
                  className="flex items-center space-x-2 text-gray-600 hover:text-purple-600 transition-colors"
                >
                  <Calendar className="h-5 w-5" />
                  <span>SM Planner</span>
                </Link>
                <div 
                  className="relative"
                  onMouseEnter={() => setMoreDropdownOpen(true)}
                  onMouseLeave={() => setMoreDropdownOpen(false)}
                >
                  <button className="flex items-center space-x-2 text-gray-600 hover:text-purple-600 transition-colors">
                    <MoreHorizontal className="h-5 w-5" />
                    <span>More</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  {/* Dropdown menu */}
                  {moreDropdownOpen && (
                    <div className="absolute left-0 top-full pt-2 z-50">
                      <div className="bg-white shadow-lg border border-gray-200 rounded-lg w-48">
                        <Link 
                          to="/agent" 
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:bg-purple-50 hover:text-purple-600 transition-colors rounded-t-lg"
                        >
                          <Sparkles className="h-4 w-4" />
                          <span>Agent</span>
                        </Link>
                        <Link 
                          to="/categories" 
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                        >
                          <Tag className="h-4 w-4" />
                          <span>Categories</span>
                        </Link>
                        <Link 
                          to="/blogs" 
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                        >
                          <BookOpen className="h-4 w-4" />
                          <span>Blogs</span>
                        </Link>
                        <Link 
                          to="/content-manager" 
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                        >
                          <LayoutGrid className="h-4 w-4" />
                          <span>Content Manager</span>
                        </Link>
                        <Link 
                          to="/content-manager-v2" 
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                        >
                          <LayoutGrid className="h-4 w-4" />
                          <span>Content Manager v2</span>
                        </Link>
                        <Link 
                          to="/settings" 
                          className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:bg-purple-50 hover:text-purple-600 transition-colors rounded-b-lg"
                        >
                          <Settings className="h-4 w-4" />
                          <span>Settings</span>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
                {/* Hidden menu items - Extraction and Admin */}
                {/* <Link 
                  to="/extraction" 
                  className="flex items-center space-x-2 text-gray-600 hover:text-purple-600 transition-colors"
                >
                  <Download className="h-5 w-5" />
                  <span>Extraction</span>
                </Link>
                <Link 
                  to="/admin" 
                  className="flex items-center space-x-2 text-gray-600 hover:text-purple-600 transition-colors"
                >
                  <Shield className="h-5 w-5" />
                  <span>Admin</span>
                </Link> */}
            <button
              onClick={signOut}
              className="flex items-center space-x-2 text-gray-600 hover:text-red-600 transition-colors"
            >
              <LogOut className="h-5 w-5" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen bg-gray-50">
          <Navigation />
          
          {/* Main Content */}
          <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />
              
              {/* Protected routes */}
              <Route path="/" element={<ProtectedRoute><V2Workflow /></ProtectedRoute>} />
              <Route path="/upload" element={<ProtectedRoute><UploadPage /></ProtectedRoute>} />
              <Route path="/articles" element={<ProtectedRoute><ArticlesList /></ProtectedRoute>} />
              <Route path="/articles/:id" element={<ProtectedRoute><ArticleEditor /></ProtectedRoute>} />
              <Route path="/workflow/:id" element={<ProtectedRoute><WorkflowHub /></ProtectedRoute>} />
              <Route path="/v2" element={<ProtectedRoute><V2Workflow /></ProtectedRoute>} />
                  <Route path="/v3" element={<ProtectedRoute><V3Workflow /></ProtectedRoute>} />
                  <Route path="/v5" element={<ProtectedRoute><V5BatchSystem /></ProtectedRoute>} />
                  <Route path="/videos" element={<ProtectedRoute><VideosPage /></ProtectedRoute>} />
                  <Route path="/videos/riverside" element={<ProtectedRoute><RiversideVideos /></ProtectedRoute>} />
                  <Route path="/sm-planner" element={<ProtectedRoute><SMPlanner /></ProtectedRoute>} />
                  <Route path="/extraction" element={<ProtectedRoute><ExtractionMonitor /></ProtectedRoute>} />
                  <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
                  <Route path="/v2-projects" element={<ProtectedRoute><V2Projects /></ProtectedRoute>} />
                  <Route path="/agent" element={<ProtectedRoute><AgentPage /></ProtectedRoute>} />
                  <Route path="/categories" element={<ProtectedRoute><CategoriesPage /></ProtectedRoute>} />
                  <Route path="/blogs" element={<ProtectedRoute><BlogsPage /></ProtectedRoute>} />
                  <Route path="/content-manager" element={<ProtectedRoute><ContentManager /></ProtectedRoute>} />
                  <Route path="/content-manager-v2" element={<ProtectedRoute><ContentManagerV2 /></ProtectedRoute>} />
                  <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            </Routes>
          </main>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;
